import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import { Spinner } from 'components/ui/spinner';
import { useUser } from 'lib/auth';
import {
    DeployableName,
    getDeployableAccessDecision,
} from 'lib/deployable-access';

export const DeployableAccessBoundary = ({
    children,
    deployable,
}: {
    children: React.ReactNode;
    deployable: DeployableName;
}) => {
    const userQuery = useUser();
    const { data: user, isLoading } = userQuery;
    const queryClient = useQueryClient();
    const previousOrganizationId = useRef<string | null>(null);
    const decision = useMemo(
        () =>
            user
                ? getDeployableAccessDecision({
                      deployable,
                      organizationType: user.organization.type,
                      deploymentUrl: user.deployment_url,
                      currentOrigin: window.location.origin,
                  })
                : null,
        [deployable, user],
    );

    useEffect(() => {
        if (decision?.kind === 'redirect') {
            window.location.replace(decision.url);
        }
    }, [decision]);

    useEffect(() => {
        const organizationId = user?.organization.id ?? null;
        if (
            previousOrganizationId.current &&
            organizationId &&
            previousOrganizationId.current !== organizationId
        ) {
            queryClient.removeQueries({ queryKey: ['hospital'] });
        }
        previousOrganizationId.current = organizationId;
    }, [queryClient, user?.organization.id]);

    if (userQuery.isError) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-muted/20 p-6">
                <div className="max-w-md space-y-3 text-center">
                    <h1 className="text-2xl font-semibold">
                        Organization unavailable
                    </h1>
                    <p className="text-muted-foreground">
                        Your organization and deployment access could not be
                        loaded.
                    </p>
                    <Button onClick={() => void userQuery.refetch()}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    if (isLoading || !decision || decision.kind === 'redirect') {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                <Spinner size="xl" />
            </div>
        );
    }

    if (decision.kind === 'denied') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-muted/20 p-6">
                <div className="max-w-md space-y-3 text-center">
                    <h1 className="text-2xl font-semibold">
                        Deployment unavailable
                    </h1>
                    <p className="text-muted-foreground">
                        This organization does not have access to this Atomix
                        deployment. Contact support if you believe this is an
                        error.
                    </p>
                </div>
            </div>
        );
    }

    return children;
};
